import React, { useMemo } from "react";
import clsx from "clsx";
import {
  TextTooltip,
  ProgressImage,
  ColoredImage,
  Badge,
  HyphenIcon,
} from "@transfr-inc/dashboard-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import certificateIcon from "../../../public/images/certificate-icon.png";
import microcredentialIcon from "../../../public/images/microcredential-icon.png";

import "./credential.scss";
import { getCredentialType } from "@components/common/programs/programs.utils";
import { ConfettiOverlay } from "@components/confetti-overlay";

const getPercent = (fraction) => {
  return Math.round((fraction?.numerator / fraction?.denominator) * 100);
};

export const Credential = ({ isMain, courseInfo, userCredInfo, className }) => {
  const progress = courseInfo?.progressMap;
  const showBadges = Boolean(userCredInfo) == false;

  const progressPercent = useMemo(() => {
    if (isNaN(progress)) {
      const fraction = getPercent(progress);
      if (!fraction) return 0;
      return fraction;
    } else {
      return progress;
    }
  }, [progress]);

  useMemo(() => {
    if (
      !userCredInfo ||
      !userCredInfo?.credentialInfo ||
      !courseInfo ||
      !isMain
    )
      return { credentialId: null };

    const cred = userCredInfo.credentialInfo?.find(
      (c) => c.groupId === courseInfo.groupId
    );
    return {
      credentialId: cred ? cred.credentialId : null,
      credentialName: courseInfo.title,
    };
  }, [userCredInfo, courseInfo]);

  const showConfetti = progressPercent === 100;

  const tooltipText = `${courseInfo?.progressMap?.numerator} / ${courseInfo?.progressMap?.denominator}`;
  const mainProgressProps = showBadges
    ? { strokeWidth: 6, showComplete: true }
    : {
        strokeWidth: 6,
        progress: progressPercent,
        tooltipText,
        isLarge: true,
        showComplete: true,
      };
  const minorProgressProps = showBadges
    ? { strokeWidth: 6, showComplete: true }
    : {
        strokeWidth: 6,
        progress: progressPercent,
        tooltipText,
        showComplete: true,
      };

  return (
    <>
      {isMain ? (
        <>
          <div className="main-certificate-info-container">
            <div className="main-certificate-info-container-header">
              <div className="main-title">{courseInfo.title}</div>
              {showBadges && (
                <div className="badge-container">
                  <Badge
                    value={
                      courseInfo?.totalAwarded > 0 ? (
                        courseInfo?.totalAwarded
                      ) : (
                        <HyphenIcon />
                      )
                    }
                  />
                </div>
              )}
            </div>
            <div className="main-type">
              {getCredentialType(courseInfo.type)}
            </div>
            <div className="main-description">
              {courseInfo.description.replace(/<[^>]+>/g, "")}
            </div>
          </div>

          <div className={clsx("main-certificate-icon-wrapper")}>
            {!showBadges && (
              <div
                className={clsx(
                  "confetti-wrapper",
                  showConfetti && "show-confetti"
                )}
              >
                <ConfettiOverlay
                  shouldRun={userCredInfo?.hasNew}
                  loopCount={1}
                />
              </div>
            )}
            <div className={clsx("main-certificate-icon", className)}>
              <ProgressImage {...mainProgressProps}>
                <ColoredImage
                  src={certificateIcon}
                  srcSet={`${certificateIcon} 1x, ${certificateIcon} 2x`}
                  alt="Main Certificate"
                  colored={showBadges ?? undefined}
                />
              </ProgressImage>
            </div>
          </div>
        </>
      ) : (
        <div className={clsx("minor-certificate", className)}>
          <div className="certificate-icon">
            <ProgressImage {...minorProgressProps}>
              <ColoredImage
                src={microcredentialIcon}
                srcSet={`${microcredentialIcon} 1x, ${microcredentialIcon} 2x`}
                alt="Certificate"
                colored={showBadges ?? undefined}
              />
            </ProgressImage>
          </div>
          <div className="minor-certificate-info-container">
            <div className="minor-certificate-info-container-header">
              <div className="minor-title">{courseInfo.title}</div>
              {showBadges && (
                <div className="badge-container">
                  <Badge
                    value={
                      courseInfo?.totalAwarded > 0 ? (
                        courseInfo?.totalAwarded
                      ) : (
                        <HyphenIcon />
                      )
                    }
                  />
                </div>
              )}
            </div>
            <div
              className={clsx(
                "minor-type",
                progressPercent !== 100 && "incomplete"
              )}
            >
              {getCredentialType(courseInfo.type)}{" "}
              <TextTooltip
                text={courseInfo.description.replace(/<[^>]+>/g, "")}
              >
                <FontAwesomeIcon
                  className="info"
                  icon="fa-regular fa-circle-info"
                />
              </TextTooltip>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
